import { type Slot } from '../models/virtual-table-column.model';

export interface CalculateWidthsArgs {
  tableWidth: number;
  columnWidths: (number | undefined)[];
  selectColumnExists: boolean;
  actionsColumnExists: boolean;
  actionsWidth?: number;
  displayedColumns: string[];
  defaultColumnWidth: number;
  selectColumnDefaultWidth: number;
  actionsColumnDefaultWidth: number;
}

export interface CalculateWidthsResult {
  cellsWidth: number[];
  headerActionsWidth: number;
}

interface ResolveActionsWidthParams {
  /** Width measured from the rendered action cells, if available. */
  measuredWidth: number | undefined;
  /** Whether the other columns leave room the actions column can grow into. */
  hasRoomToGrow: boolean;
  /** Width the column fills when there is room (the leftover table space). */
  growWidth: number;
  /** Minimum width the column collapses to when the columns overflow the table. */
  tightWidth: number;
}

export function getMaxWidthFromElements(elements: HTMLElement[]): number {
  if (!elements.length) {
    return 0;
  }
  return Math.max(0, ...elements.map((el) => el.getBoundingClientRect().width));
}

export function filterColumnsBySlot(
  slot: Slot,
  columns: string[],
  firstColumnsAlwaysVisible: number,
  lastColumnAlwaysVisible: boolean,
  selectColumnName: string,
  actionsColumnName: string,
): { columns: string[]; indexes: number[] } {
  const selectColumnExists = columns[0] === selectColumnName;
  let stickyLeftCount = 0;

  if (firstColumnsAlwaysVisible > 0) {
    stickyLeftCount = firstColumnsAlwaysVisible;

    if (selectColumnExists) {
      stickyLeftCount += 1;
    }
  }

  const rightSlotVisible = lastColumnAlwaysVisible && columns.at(-1) === actionsColumnName;
  const stickyRightCount = rightSlotVisible ? 1 : 0;

  let start = 0;
  let end = 0;

  switch (slot) {
    case 'left':
      start = 0;
      end = stickyLeftCount;
      break;
    case 'right':
      start = columns.length - stickyRightCount;
      end = columns.length;
      break;
    case 'scroll':
      start = stickyLeftCount;
      end = columns.length - stickyRightCount;
      break;
  }

  if (start >= end) {
    return { columns: [], indexes: [] };
  }

  const filteredColumns = columns.slice(start, end);
  return {
    columns: filteredColumns,
    indexes: filteredColumns.map((_, i) => start + i),
  };
}

export function getHeaderRowTemplateColumns(
  cellsWidth: number[],
  displayedColumns: string[],
  selectColumnName: string,
  actionsColumnName: string,
  firstColumnsAlwaysVisible: number,
  lastColumnAlwaysVisible: boolean,
): string {
  const sumSlotWidth = (slot: Slot): number =>
    filterColumnsBySlot(
      slot,
      displayedColumns,
      firstColumnsAlwaysVisible,
      lastColumnAlwaysVisible,
      selectColumnName,
      actionsColumnName,
    ).indexes.reduce((sum, index) => sum + cellsWidth[index], 0);

  const actionsIsSticky = lastColumnAlwaysVisible && displayedColumns.includes(actionsColumnName);

  const rowGridTemplateCols: string[] = [];

  if (firstColumnsAlwaysVisible) {
    rowGridTemplateCols.push(`${sumSlotWidth('left')}px`);
  }

  rowGridTemplateCols.push(actionsIsSticky ? `${sumSlotWidth('scroll')}px` : '1fr');

  if (actionsIsSticky) {
    rowGridTemplateCols.push(`${sumSlotWidth('right')}px`);
  }

  return rowGridTemplateCols.join(' ');
}

function buildCellsWidth(
  displayedColumns: string[],
  displayedColumnsDefsWidths: (number | undefined)[],
  selectColumnExists: boolean,
  actionsColumnExists: boolean,
  selectColumnDefaultWidth: number,
  actionsWidth: number,
  defaultWidth: number,
): number[] {
  const cells = new Array(displayedColumns.length).fill(defaultWidth);

  for (let index = 0; index < displayedColumnsDefsWidths.length; index++) {
    const width = displayedColumnsDefsWidths[index];
    if (width !== undefined) {
      const target = index + (selectColumnExists ? 1 : 0);
      cells[target] = width;
    }
  }

  if (selectColumnExists) {
    cells[0] = selectColumnDefaultWidth;
  }
  if (actionsColumnExists) {
    cells[cells.length - 1] = actionsWidth;
  }

  return cells;
}

function resolveActionsWidth({
  measuredWidth,
  hasRoomToGrow,
  growWidth,
  tightWidth,
}: ResolveActionsWidthParams): number {
  if (hasRoomToGrow) {
    return measuredWidth ?? growWidth;
  }

  return Math.max(measuredWidth ?? 0, tightWidth);
}

export function calculateWidths({
  actionsWidth,
  actionsColumnDefaultWidth,
  selectColumnDefaultWidth,
  actionsColumnExists,
  displayedColumns,
  defaultColumnWidth,
  selectColumnExists,
  tableWidth,
  columnWidths,
}: CalculateWidthsArgs): CalculateWidthsResult {
  const hasAllWidths = columnWidths.every((w): w is number => w !== undefined);
  const hasSomeWidths = columnWidths.some((w) => w !== undefined);

  let headerActionsWidth: number;
  let cellsWidth: number[];

  if (hasAllWidths) {
    const totalColsWidth =
      columnWidths.reduce((sum, w) => sum + w, 0) +
      (selectColumnExists ? selectColumnDefaultWidth : 0);

    headerActionsWidth = Math.max(
      tableWidth - totalColsWidth,
      actionsWidth ?? 0,
      actionsColumnDefaultWidth,
    );

    cellsWidth = buildCellsWidth(
      displayedColumns,
      columnWidths,
      selectColumnExists,
      actionsColumnExists,
      selectColumnDefaultWidth,
      headerActionsWidth,
      defaultColumnWidth,
    );

    if (!actionsColumnExists && tableWidth > 0 && tableWidth > totalColsWidth) {
      cellsWidth[cellsWidth.length - 1] += tableWidth - totalColsWidth;
    }

    return { cellsWidth, headerActionsWidth };
  }

  const resizedWidths = columnWidths.filter((w): w is number => w !== undefined);
  const resizedTotal = resizedWidths.reduce((sum, w) => sum + w, 0);
  const resizedCount = resizedWidths.length;

  let availableWidth = tableWidth - resizedTotal;
  let visibleColumnsCount = displayedColumns.length;

  if (selectColumnExists) {
    availableWidth -= selectColumnDefaultWidth;
    visibleColumnsCount--;
  }

  if (actionsColumnExists && actionsWidth) {
    availableWidth -= actionsWidth;
    visibleColumnsCount--;
  }

  const meanWidth = availableWidth / Math.max(visibleColumnsCount - resizedCount, 1);
  const baseWidth = Math.max(meanWidth, defaultColumnWidth);

  headerActionsWidth = resolveActionsWidth({
    measuredWidth: actionsWidth,
    hasRoomToGrow: meanWidth >= defaultColumnWidth,
    growWidth: meanWidth,
    tightWidth: hasSomeWidths
      ? actionsColumnDefaultWidth
      : Math.max(
          tableWidth - defaultColumnWidth * displayedColumns.length,
          actionsColumnDefaultWidth,
        ),
  });

  cellsWidth = buildCellsWidth(
    displayedColumns,
    columnWidths,
    selectColumnExists,
    actionsColumnExists,
    selectColumnDefaultWidth,
    headerActionsWidth,
    baseWidth,
  );

  return { cellsWidth, headerActionsWidth };
}
